import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Trigger a browser file download from a Blob (e.g. from an authenticated API response). */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Format a date with an explicit locale.
 * Accepts a BCP-47 locale string (e.g. `ar-EG-u-nu-latn`) or a bare language
 * code (`"ar"` → `ar-EG`, `"en"` → `en-GB`) for backwards compatibility.
 * Never use Intl.DateTimeFormat(undefined) — it picks up the OS locale,
 * which causes Arabic-Indic numerals to appear in English/LTR UI on Arabic-OS machines.
 */
export function formatDate(
  date: Date | string,
  langOrLocale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
  const locale =
    langOrLocale === "ar" ? "ar-EG" :
    langOrLocale === "en" ? "en-GB" :
    langOrLocale;
  return new Intl.DateTimeFormat(locale, options).format(
    typeof date === "string" ? new Date(date) : date,
  );
}

/**
 * Format a number with an explicit BCP-47 locale string.
 * Pass `ar-EG` for Arabic-Indic digits, `ar-EG-u-nu-latn` for Arabic layout
 * with Western digits, or `en-GB` for fully Western formatting.
 * Never use Intl.NumberFormat(undefined) — same OS-locale leakage as formatDate.
 */
export function formatNum(
  n: number,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(locale, options).format(n);
}

/** User preference for digit glyphs, independent of UI language. */
export type NumberFormatPref = "western" | "arabic";

/**
 * Resolve the BCP-47 locale used for ALL numeric/date formatting.
 *
 * Decouples two concerns the app used to conflate:
 *  - **Base locale** (grouping separators, month names) follows the UI language.
 *  - **Digit glyphs** follow the user's `numberFormat` preference via the
 *    `-u-nu-` Unicode extension — `arab` (٠١٢…) or `latn` (012) — so digits are
 *    independent of language (English UI can show Arabic-Indic digits and vice versa).
 */
export function resolveNumLocale(lang: string, numberFormat: NumberFormatPref): string {
  const base = lang === "ar" ? "ar-EG" : "en-GB";
  const nu = numberFormat === "arabic" ? "arab" : "latn";
  return `${base}-u-nu-${nu}`;
}

/**
 * Convert Western numerals to Arabic-indic numerals (٠١٢٣٤٥٦٧٨٩).
 * Accepts numbers or mixed strings — only digit characters are converted,
 * so "3.1d" → "٣.١d" and "Jan 2025" → "Jan ٢٠٢٥" pass through unharmed.
 */
export function toArabicNumerals(input: number | string): string {
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return String(input).replace(/[0-9]/g, (d) => arabicDigits[Number(d)]!);
}

/** Unit suffix labels for {@link formatDuration} — sourced from i18n, never hard-coded. */
export interface DurationUnits {
  /** Hours suffix, e.g. "h" / "س" */
  h: string;
  /** Minutes suffix, e.g. "m" / "د" */
  m: string;
  /** Seconds suffix, e.g. "s" / "ث" */
  s: string;
}

/**
 * Format a duration (in seconds) into a compact, tiered, localized string.
 *
 * Tiers: under a minute → seconds only ("45s"); under an hour →
 * minutes + seconds ("5m 30s"); an hour or more → hours + minutes ("1h 5m").
 * Arabic (RTL) renders Arabic-Indic numerals; LTR renders Latin numerals.
 *
 * @param totalSeconds — duration in seconds (negative values clamp to 0)
 * @param units — localized unit suffixes from `t()` (no raw literals)
 * @param isRTL — true for Arabic-Indic numeral rendering
 */
export function formatDuration(
  totalSeconds: number,
  units: DurationUnits,
  isRTL: boolean
): string {
  const num = (v: number) => (isRTL ? toArabicNumerals(v) : String(v));
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${num(hours)}${units.h}`);
  if (hours > 0 || minutes > 0) parts.push(`${num(minutes)}${units.m}`);
  if (hours === 0) parts.push(`${num(seconds)}${units.s}`);
  return parts.join(" ");
}
